<script setup>
/*
    VuInput.js - Vue input directive

    <vu-input v-model="value" :name="name" />
    <vu-input v-model="value" :name="name" :type="switch" />
    <vu-input v-model="value" :name="name" :dataType="number" placeholder="42" :rules="set.name" />

    https://vcalendar.io/datepicker.html#form-example

    Attributes:
        v-model="value"
        auto="autocomplete name"
        type="type"
        dataType="dataType"
        label="Text"
        labelClass="Class"
        inputClass="Class"
        itemClass="Class"
        items="Switch, select or radio items"
        placeholder="Placeholder"
        rules="Validation rules"

    Examples:
        items = {
            speed: {
                "1000":  "1000",
                "10000": "10000",
                "40000": "40000",
            },
            mode: {
                "Online": "Online",
                "Offline": "Offline",
            },
        }

    Emits @update:modelValue when input is finalized (not each keystroke)
 */
import {getCurrentInstance, inject, onMounted, reactive, ref, watch} from 'vue'
import {titlecase} from '@/paks/js-polyfill'
import {State} from '@/paks/vu-state'

const instance = getCurrentInstance()

const dataToDisplay = {
    array: 'text',
    bool: 'switch',
    boolean: 'switch',
    date: 'date',
    number: 'text',
    int: 'text',
    double: 'text',
    object: 'text',
    string: 'text',
    text: 'textarea',
}

const props = defineProps({
    accept: String,
    appendIcon: String,
    autocomplete: {type: String, default: 'none'},
    background: String,
    clearable: {type: Boolean, default: false},
    chips: Boolean,
    cols: [Number, String],
    color: String,
    dataType: String,
    datetime: {type: String, default: 'datetime'},
    density: {type: String, enum: ['default', 'comfortable', 'compact'], default: 'compact'},
    disabled: Boolean,
    errors: Array,
    falseValue: {type: Boolean, default: false},
    hideDetails: {type: [String, Boolean], enum: [true, false, 'auto'], default: false},
    id: String,
    inset: {type: Boolean, default: true},
    inputClass: {type: String, default: ''},
    itemClass: {type: String, default: ''},
    itemTitle: {type: String},
    itemValue: {type: String},
    items: {type: [Array, Object], default: []},
    label: String,
    labelClass: {type: String, default: ''},
    max: [Number, Object],
    menuProps: {type: Object},
    min: [Number, Object],
    modelValue: {type: [String, Array, Boolean, Date, Number, Object], default: null},
    multiple: Boolean,
    name: String,
    numeric: Boolean,
    placeholder: String,
    popover: {
        type: Object,
        default: {
            visibility: 'focus',
            autoHide: true,
            placement: 'bottom-start',
            showDelay: 0,
            hideDelay: 110,
        },
    },
    prefix: String,
    prependIcon: String,
    returnObject: {type: Boolean},
    rows: [Number, String],
    rules: {type: Array, default: () => []},
    suffix: String,
    timezone: {type: String, default: 'local'},
    thumb: {type: Boolean, default: true},
    trueValue: {type: Boolean, default: true},
    type: String,
    variant: {type: String, default: 'underlined'},
})

const emit = defineEmits(['clear', 'update:modelValue'])

const page = reactive({
    attributes: [{dot: 'red', dates: new Date()}],
    classes: null,
    dateConfig: {},
    errors: [],
    label: null,
    name: null,
    value: null,
    timeout: null,
    type: null,
})

const inputs = inject('inputs', [])
const input = ref(null)
const picker = ref(null)
let switchMap = null

watch(() => props.modelValue, () => {
    renderControls()
})
watch(() => props.label, renderControls)
watch(
    () => props.errors,
    () => {
        renderControls()
    }
)

defineExpose({page, setError})

onMounted(() => {
    page.name = (props.name || props.label || '').toLowerCase()
    page.name = page.name.replace(/ /g, '-')
    page.classes = `vcol vcol-${props.cols || 12} ${State.app.dark ? 'dark' : 'light'}`
    page.value = props.modelValue

    switchMap = null
    if (props.type == 'switch' && props.items) {
        if (Array.isArray(props.items) && props.items.length == 2) {
            switchMap = {}
            switchMap[false] = props.items[0]
            switchMap[true] = props.items[1]
        } else if (props.items[true] && props.items[false]) {
            switchMap = props.items
        }
    }
    if (inputs?.value) {
        inputs.value[page.name] = instance
    }
    renderControls()
})

function renderControls() {
    let dataType = props.dataType || getType(props.modelValue)
    page.type = props.type || dataToDisplay[dataType]
    page.label = props.label != null ? props.label : makeTitle(props.name)

    if (dataType == 'object') {
        if (page.type == 'file') {
            page.value = props.modelValue
        } else {
            page.value = JSON.stringify(props.modelValue)
        }
    } else if (page.type == 'date' || dataType == 'date' || props.modelValue instanceof Date) {
        if (props.modelValue) {
            page.value = new Date(Date.parse(props.modelValue))
        } else {
            page.value = new Date()
        }
    } else {
        page.value = props.modelValue
    }
    if (props.errors && props.errors.length) {
        setError(props.errors)
    } else {
        page.errors = []
    }
}

function onBlur(event) {
    onInput(event?.target ? event.target.value : event)
}

/*
    Called for when the input is defocussed or on input that is inherently final (radio).
    The model value is not yet updated when this function is called.
 */
function onInput(value) {
    if (value == null) {
        return
    }
    if (page.type == 'date') {
        value = new Date(Date.parse(value))
    }
    if (page.type == 'combo' && value[props.itemValue]) {
        value = value[props.itemValue]
    }
    if (page.type == 'date') {
        value = new Date(Date.parse(value))
    }
    if (typeof value == 'string') {
        value = value.trim()
    }
    // page.value = switchMap ? switchMap[value] : value
    page.value = value
    emit('update:modelValue', page.value)
}

function onClear(event) {
    page.value = ''
    emit('update:modelValue', '')
    emit('clear', event)
}

function onEnter(event) {
    onBlur(event)
}

function getType(v) {
    if (typeof v == 'boolean') {
        return 'boolean'
    } else if (typeof v == 'string') {
        return 'string'
    } else if (typeof v == 'number') {
        return 'number'
    } else if (v instanceof Date) {
        return 'date'
    } else if (Array.isArray(v)) {
        return 'array'
    } else if (typeof v == 'object' && v != null) {
        return 'object'
    }
    return 'string'
}

function makeTitle(str = '') {
    str = str.replace(/[A-Z][a-zA-Z0-9$_]*/g, ' $&')
    let words = str.split(/[ \.]/g)
    for (var i = 0; i < words.length; i++) {
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1)
    }
    return words.join(' ')
}

function setError(messages) {
    page.errors = []
    if (messages) {
        if (!Array.isArray(messages)) {
            messages = [messages]
        }
        for (let message of messages) {
            page.errors.push(message)
        }
    }
}

//  Workaround for v-calendar not setting inputValue until the user updates
function getDate() {
    let when = page.value
    let dateFormatter = new Intl.DateTimeFormat(navigator?.lanaguage || 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
    let timeFormatter = new Intl.DateTimeFormat(navigator?.lanaguage || 'en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
    if (props.datetime == 'time') {
        return timeFormatter.format(when)
    } else if (props.datetime == 'date') {
        return dateFormatter.format(when)
    } else {
        return `${dateFormatter.format(when)} ${timeFormatter.format(when)}`
    }
}

function updateDate(updateValue, event) {
    let when = event.target.value
    if (!isNaN(new Date(Date.parse(when)))) {
        updateValue(when)
    } else {
        //  Just ignore until user corrects
    }
}

function wrapEvents(events) {
    let wrapped = Object.assign({}, events)
    delete wrapped.change
    delete wrapped.input
    return wrapped
}

function nop(event) {
    event.stopPropagation()
}
</script>

<template>
    <div class="vu-input" :class="page.classes">
        <v-text-field
            v-if="page.type == 'label'"
            :autocomplete="props.autocomplete"
            :bg-color="background"
            :class="inputClass"
            :density="density"
            :disabled="true"
            :color="color"
            :label="page.label"
            :modelValue="page.value"
            :name="page.name"
            :placeholder="placeholder"
            :prefix="prefix"
            :suffix="suffix"
            :type="type"
            :variant="props.variant"
            @mousedown.stop="nop"
            ref="input" />

        <v-text-field
            v-if="page.type == 'text' || page.type == 'password'"
            :append-icon="appendIcon"
            :autocomplete="props.autocomplete"
            :bg-color="background"
            :type="type"
            :class="inputClass"
            :clearable="clearable"
            :color="color"
            :density="density"
            :disabled="disabled"
            :error="page.errors && page.errors.length > 0"
            :error-messages="page.errors"
            :hide-details="hideDetails"
            :id="id ? id : `id-${page.name}`"
            :label="page.label"
            :modelValue="page.value"
            :name="page.name"
            :placeholder="placeholder"
            :prefix="prefix"
            :prepend-icon="prependIcon"
            :rules="rules"
            :suffix="suffix"
            :variant="props.variant"
            ref="input"
            @click:clear="onClear"
            @mousedown.stop="nop"
            @keyup.enter="onEnter"
            @blur="onBlur"
        />

        <v-textarea
            v-if="page.type == 'textarea'"
            :color="color"
            :bg-color="background"
            :autocomplete="props.autocomplete"
            :clearable="clearable"
            :density="density"
            :disabled="disabled"
            :error="page.errors && page.errors.length > 0"
            :error-messages="page.errors"
            :hide-details="hideDetails"
            :label="page.label"
            :modelValue="page.value"
            :name="page.name"
            :placeholder="placeholder"
            :rows="rows"
            :rules="rules"
            :variant="props.variant"
            ref="input"
            @mousedown.stop="nop"
            @click:clear="onClear"
            @blur="onBlur"
        />

        <v-checkbox
            v-if="page.type == 'checkbox'"
            :autocomplete="props.autocomplete"
            :bg-color="background"
            :class="inputClass"
            :color="color || 'primary'"
            :density="density"
            :disabled="disabled"
            :error="page.errors && page.errors.length > 0"
            :error-messages="page.errors"
            :hide-details="hideDetails"
            :label="page.label"
            :modelValue="page.value"
            :name="page.name"
            :variant="props.variant"
            inline
            @mousedown.stop="nop"
            @update:modelValue="onInput"></v-checkbox>

        <v-radio-group
            v-if="page.type == 'radio'"
            inline
            :autocomplete="props.autocomplete"
            :bg-color="background"
            :class="inputClass"
            :color="color || 'primary'"
            :density="density"
            :disabled="disabled"
            :error="page.errors && page.errors.length > 0"
            :error-messages="page.errors"
            :hide-details="hideDetails"
            :label="page.label"
            :modelValue="page.value"
            :name="page.name"
            :variant="props.variant"
            @mousedown.stop="nop"
            @update:modelValue="onInput">
            <v-radio
                v-if="Array.isArray(items)"
                v-for="label in items"
                :class="itemClass"
                :key="`radio-${label}`"
                :label="label"
                :value="label" />
            <v-radio
                v-else
                v-for="(value, name) in items"
                :class="itemClass"
                :key="`radio-${name}`"
                :label="name"
                :value="value" />
        </v-radio-group>

        <v-select
            v-if="page.type == 'select'"
            :autocomplete="props.autocomplete"
            :bg-color="background"
            :class="inputClass"
            :color="color"
            :density="density"
            :disabled="disabled"
            :error="page.errors && page.errors.length > 0"
            :error-messages="page.errors"
            :hide-details="hideDetails"
            :items="items"
            :item-value="itemValue"
            :item-title="itemTitle"
            :label="page.label"
            :menu-props="menuProps"
            :modelValue="page.value"
            :multiple="multiple"
            :name="page.name"
            :return-object="returnObject"
            :rules="rules"
            :variant="props.variant"
            nofilter
            @mousedown.stop="nop"
            @update:modelValue="onInput" />

        <!-- Switch style checkbox -->
        <div v-if="page.type == 'switch'">
            <v-label v-if="page.label">{{ titlecase(page.label) }}</v-label>
            <v-switch
                v-if="page.type == 'switch'"
                :autocomplete="props.autocomplete"
                :inset="inset"
                :color="color || 'primary'"
                :class="inputClass"
                :density="density"
                :disabled="disabled"
                :error="page.errors && page.errors.length > 0"
                :error-messages="page.errors"
                :false-value="switchMap ? switchMap[false] : props.falseValue"
                :hide-details="hideDetails"
                :label="props.suffix"
                :menu-props="menuProps"
                :modelValue="page.value"
                :name="page.name"
                :true-value="switchMap ? switchMap[true] : props.trueValue"
                :variant="props.variant"
                @mousedown.stop="nop"
                @update:modelValue="onInput" />
        </div>

        <div v-if="page.type == 'combo'">
            <v-combobox
                :autocomplete="autocomplete"
                :bg-color="background"
                :class="inputClass"
                :clearable="clearable"
                :chips="chips"
                :color="color"
                :density="density"
                :disabled="disabled"
                :error="page.errors && page.errors.length > 0"
                :error-messages="page.errors"
                :hide-details="hideDetails"
                :items="items"
                :item-value="itemValue"
                :item-title="itemTitle"
                :label="page.label"
                :menu-props="menuProps"
                :modelValue="page.value"
                :multiple="multiple"
                :name="page.name"
                :rules="rules"
                :variant="props.variant"
                :return-object="false"
                @click:clear="onClear"
                @mousedown.stop="nop"
                @update:modelValue="onInput"
            />
        </div>

        <div v-if="page.type == 'slider'">
            <v-slider
                :append-icon="appendIcon"
                :autocomplete="props.autocomplete"
                :bg-color="background"
                :class="inputClass + !page.label ? ' ml-3' : ''"
                :color="color"
                :density="density"
                :disabled="disabled"
                :error="page.errors && page.errors.length > 0"
                :error-messages="page.errors"
                :hide-details="hideDetails"
                :label="page.label"
                :max="max"
                :min="min"
                :modelValue="page.value"
                :name="page.name"
                :prepend-icon="prependIcon"
                :step="1"
                :thumb-label="thumb"
                :variant="props.variant"
                @mousedown.stop="nop"
                @update:modelValue="onInput">
                <template v-slot:append>
                    <v-text-field
                        v-if="props.numeric"
                        v-model="page.value"
                        density="compact"
                        class="slider-number"
                        type="number"
                        variant="plain"
                        hide-details 
                        @update:modelValue="onInput" />
                </template>
            </v-slider>
        </div>

        <!-- vcalendar picker -->
        <div v-if="page.type == 'date'" class="date-picker">
            <vu-date-picker
                ref="picker"
                v-model="page.value"
                :attributes="page.attributes"
                :autocomplete="props.autocomplete"
                :is-dark="State.app.dark"
                :density="density"
                :hide-details="hideDetails"
                :max-date="max"
                :min-date="min"
                :mode="datetime"
                :name="page.name"
                :popover="popover"
                :timezone="timezone == 'local' ? null : 'UTC'"
                :update-on-input="true"
                :variant="props.variant"
                @mousedown.stop="nop"
                @blur="onBlur">
                <template #default="{inputEvents, inputValue, updateValue}">
                    <v-text-field
                        prepend-icon="$calendar"
                        v-on="wrapEvents(inputEvents)"
                        class="date-input"
                        :model-value="getDate(inputValue)"
                        :density="density"
                        :disabled="disabled"
                        :error="page.errors && page.errors.length > 0"
                        :error-messages="page.errors"
                        :label="page.label"
                        :name="page.name"
                        :placeholder="placeholder"
                        :prefix="prefix"
                        :rules="rules"
                        :suffix="suffix"
                        @update:modelValue="updateDate(updateValue, $event)" />
                </template>
            </vu-date-picker>
        </div>
        <div v-if="page.type == 'file'">
            <v-label v-if="page.label">{{ titlecase(page.label) }}</v-label>
            <v-file-input
                class="mb-4"
                show-size
                :accept="accept"
                :clearable="clearable"
                :density="density"
                :error="page.errors && page.errors.length > 0"
                :error-messages="page.errors"
                :hide-details="hideDetails"
                :label="placeholder"
                :modelValue="page.value"
                :multiple="multiple"
                :name="page.name"
                :variant="props.variant"
                ref="input"
                @click:clear="onClear"
                @mousedown.stop="nop"
                @update:modelValue="onInput" />
        </div>
    </div>
</template>

<style lang="scss">
.vu-input {
    flex: 1 1 auto;
    max-width: 100%;
    min-width: 0px;
    --v-disabled-opacity: 1;
    .v-textarea {
        .v-field-label {
            color: rgb(var(--v-theme-text-darken-2));
            padding-left: 4px;
        }
        .v-field--active .v-field-label {
            padding: 0;
        }
        textarea {
            font-size: 1rem;
            line-height: 1.25em !important;
            margin: 0;
            // Big padding to allow for errors. Was 18px
            padding: 18px 8px 50px 3px;
        }
        textarea::placeholder {
            padding-top: 16px;
        }
    }
    .v-switch:not(.v-switch--inset) {
        .v-selection-control__wrapper {
            _padding-left: 14px;
        }
    }
    .v-checkbox {
        .v-selection-control__wrapper,
        .v-selection-control__input {
            justify-content: left !important;
        }
        label {
            opacity: var(--v-medium-emphasis-opacity);
            color: rgba(var(--v-theme-text));
        }
    }
    .v-radio-group {
        .v-input__control {
            .v-selection-control-group {
                padding-inline-start: 0 !important;
                padding-left: 0 !important;
                margin-left: -4px !important;
            }
            .v-selection-control {
                margin-right: 4px;
            }
            .v-label {
                margin-inline-start: 0 !important;
            }
        }
    }
    .v-combobox--chips {
        .v-field {
            padding-bottom: 4px;
        }
    }
    .v-field__clearable {
        padding-bottom: 4px;
        font-size: 12px;
    }
    .v-slider {
        .v-input__prepend {
            margin-right: 8px;
            label {
                margin-right: 0;
            }
        }
    }
    .v-slider.v-input--horizontal {
        margin-inline: 0 0 !important;
    }
    .v-slider-thumb {
        color: rgb(var(--v-theme-accent)) !important;
        border-radius: 50%;
    }
    .v-slider-track {
        background: rgb(var(--v-theme-primary)) !important;
    }
    .v-slider-track__fill {
        background: rgb(var(--v-theme-primary)) !important;
    }
    .slider-number {
        width: 50px;
        input {
            text-align: right;
            padding-inline: 0 0 !important;
        }
    }
    ::placeholder {
        font-size: 1rem;
    }
    overflow: visible;
    .vc-popover-content-wrapper {
        position: absolute;
        z-index: 150;
    }
}
.vu-input.dark {
    .v-switch--inset {
        input[checked] + .v-switch__thumb {
            background-color: rgb(var(--v-theme-accent));
        }
        input:not([checked]) + .v-switch__thumb {
            background-color: rgb(var(--v-theme-surface-lighten-2));
        }
    }
    .v-switch:not(.v-switch--inset) {
        input[checked] + .v-switch__thumb {
            background-color: rgb(var(--v-theme-accent));
        }
        input:not([checked]) + .v-switch__thumb {
            background-color: rgb(var(--v-theme-surface-lighten-2));
        }
    }
}
.vu-input.light {
    .v-switch:not(.v-switch--inset) {
        .v-switch__thumb {
            background-color: rgb(var(--v-theme-surface)) !important;
        }
    }
    .v-switch--inset {
        .v-switch__thumb {
            background-color: rgb(var(--v-theme-surface)) !important;
        }
    }
}
.v-select__content {
    .v-list {
        background: rgb(var(--v-theme-background-lighten-1)) !important;
    }
}
.date-picker {
    .v-input__prepend {
        margin-right: 8px;
    }
}
.date-input {
    .v-field {
        font-size: 14px !important;
    }
}
</style>

<style lang="scss" scoped>
.pick-items {
    font-size: 1rem;
}
.v-list-item {
    min-height: 30px !important;
}
.v-list-item__content {
    padding: 6px 0 6px 0 !important;
}
.v-list {
    padding: 0;
}
</style>
